import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, MessageCircle, Clock, Trophy, Users, ArrowLeft, UserPlus, X, Check, User, Trash2, Plus, Building2 } from "lucide-react";
import { useNotifications } from '@/hooks/useNotifications';
import { useFriends } from '@/hooks/useFriends';
import { useChats, Chat, createOrGetChat, ChatContactType } from '@/hooks/useChats';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from "sonner";
import { useEffect, useState, useMemo } from "react";
import NewConversationDrawer from './NewConversationDrawer';
import { ChatContact, useContactsForChat } from '@/hooks/useContactsForChat';

interface NotificationsAndChatsProps {
  onNavigate: (screen: string, data?: any) => void;
  mode?: 'notifications' | 'chats';
}

const NotificationsAndChats = ({ onNavigate, mode = 'notifications' }: NotificationsAndChatsProps) => {
  const { user: authUser } = useAuth();
  const { t } = useLanguage();
  const { notifications, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();
  const { followUser, getRelationshipStatus } = useFriends();
  const { chats, loading: chatsLoading, deleteChat } = useChats();
  const { friendContacts, organizerContacts } = useContactsForChat();

  // Swipe state for notifications
  const [swipedId, setSwipedId] = useState<string | null>(null);
  // Swipe state for chats
  const [swipedChatId, setSwipedChatId] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  // New conversation drawer state
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  // Chat filter state
  const [chatFilter, setChatFilter] = useState<'all' | 'friends' | 'organizers'>('all');

  // Classify chats by contact type
  const classifiedChats = useMemo(() => {
    const friendIds = new Set(friendContacts.map(f => f.id));
    const organizerIds = new Set(organizerContacts.map(o => o.id));

    return chats.map(chat => ({
      ...chat,
      contact_type: friendIds.has(chat.other_user?.id || '')
        ? 'friend' as ChatContactType
        : organizerIds.has(chat.other_user?.id || '')
          ? 'organizer' as ChatContactType
          : 'other' as ChatContactType
    }));
  }, [chats, friendContacts, organizerContacts]);

  // Filter chats based on selected filter
  const filteredChats = useMemo(() => {
    if (chatFilter === 'all') return classifiedChats;
    if (chatFilter === 'friends') return classifiedChats.filter(c => c.contact_type === 'friend');
    if (chatFilter === 'organizers') return classifiedChats.filter(c => c.contact_type === 'organizer');
    return classifiedChats;
  }, [classifiedChats, chatFilter]);

  // Get empty state message based on filter
  const getEmptyStateMessage = () => {
    if (chatFilter === 'friends') {
      return {
        title: t('notifications.noChatsFriends'),
        description: t('notifications.noChatsFriendsDesc')
      };
    }
    if (chatFilter === 'organizers') {
      return {
        title: t('notifications.noChatsOrganizers'),
        description: t('notifications.noChatsOrganizersDesc')
      };
    }
    return {
      title: t('notifications.noChatsAll'),
      description: t('notifications.noChatsAllDesc')
    };
  };

  const minSwipeDistance = 50;

  // Helper function to format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.timeNow');
    if (diffMins < 60) return t('notifications.timeMinAgo').replace('{n}', String(diffMins));
    if (diffHours < 24) return t('notifications.timeHoursAgo').replace('{n}', String(diffHours));
    if (diffDays < 7) return t('notifications.timeDaysAgo').replace('{n}', String(diffDays));
    return date.toLocaleDateString();
  };

  const onTouchStart = (e: React.TouchEvent, notificationId: string) => {
    if (swipedId && swipedId !== notificationId) {
      setSwipedId(null);
    }
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = (notificationId: string) => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setSwipedId(notificationId);
    } else if (isRightSwipe) {
      setSwipedId(null);
    }
  };

  const handleDelete = async (notificationId: string) => {
    await deleteNotification(notificationId);
    setSwipedId(null);
    toast.success(t('notifications.deleted'));
  };

  const handleDeleteAll = async () => {
    await deleteAllNotifications();
    setSwipedId(null);
    toast.success(t('notifications.allDeleted'));
  };

  // Mark all notifications as read when user opens notifications screen
  useEffect(() => {
    if (mode === 'notifications' && notifications.length > 0) {
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        markAllAsRead();
      }
    }
  }, [mode, notifications, markAllAsRead]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'follow_notification':
        return Users;
      case 'event_reminder':
        return Clock;
      case 'achievement':
        return Trophy;
      case 'partner_invitation':
        return Users;
      case 'new_registration':
        return UserPlus;
      default:
        return Bell;
    }
  };

  const getNotificationColors = (type: string) => {
    switch (type) {
      case 'follow_notification':
        return { color: "text-green-600", bgColor: "bg-green-50" };
      case 'event_reminder':
        return { color: "text-orange-600", bgColor: "bg-orange-50" };
      case 'achievement':
        return { color: "text-purple-600", bgColor: "bg-purple-50" };
      case 'partner_invitation':
        return { color: "text-[#3D6B4F]", bgColor: "bg-emerald-50" };
      case 'new_registration':
        return { color: "text-emerald-600", bgColor: "bg-emerald-50" };
      default:
        return { color: "text-gray-600", bgColor: "bg-gray-50" };
    }
  };

  const handleFollowBack = async (notification: any) => {
    try {
      const actionUrl = notification.action_url;
      const followerIdMatch = actionUrl?.match(/follower_id=([^&]+)/);
      const followerId = followerIdMatch?.[1];
      
      if (!followerId) {
        toast.error(t('notifications.processError'));
        return;
      }
      
      console.log('Starting follow back process for follower:', followerId);
      
      const result = await followUser(followerId);
      
      if (result.error) {
        console.error('Follow back error:', result.error);
        toast.error(result.error);
      } else {
        console.log('Follow back successful');
        toast.success(t('notifications.followedBack'));
        markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Error following back:', error);
      toast.error(t('notifications.followBackError'));
    }
  };

  // Swipe handlers for chats
  const onChatTouchStart = (e: React.TouchEvent, chatId: string) => {
    if (swipedChatId && swipedChatId !== chatId) {
      setSwipedChatId(null);
    }
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onChatTouchEnd = (chatId: string) => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      setSwipedChatId(chatId);
    } else if (distance < -minSwipeDistance) {
      setSwipedChatId(null);
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      await deleteChat(chatId);
      setSwipedChatId(null);
      toast.success(t('notifications.chatDeleted'));
    } catch (error) {
      toast.error(t('notifications.chatDeleteError'));
    }
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    
    if (notification.type === "partner_invitation") {
      if (notification.action_url) {
        const invitationIdMatch = notification.action_url.match(/\/invitation\/([^\/]+)/);
        const invitationId = invitationIdMatch?.[1];
        if (invitationId) {
          window.location.href = `/invitation/${invitationId}`;
        }
      }
    } else if (notification.type === "split_payment") {
      if (notification.action_url) {
        const eventIdMatch = notification.action_url.match(/\/tournament\/([^\/]+)/);
        const eventId = eventIdMatch?.[1];
        if (eventId) {
          onNavigate('event-registration', { eventId });
        } else {
          // Fallback: old format /split-payment/{sessionId}
          const sessionMatch = notification.action_url.match(/\/split-payment\/(.+)/);
          const sessionId = sessionMatch?.[1];
          if (sessionId) {
            supabase
              .from('split_payments')
              .select('event_id')
              .eq('payer_checkout_session_id', sessionId)
              .maybeSingle()
              .then(({ data }) => {
                if (data?.event_id) {
                  onNavigate('event-registration', { eventId: data.event_id });
                }
              });
          }
        }
      }
    } else if (notification.type === "invitation") {
      onNavigate("partner-invitation-confirmation", notification);
    } else if (notification.action_url) {
      console.log('Navigate to:', notification.action_url);
    }
  };

  const handleViewProfile = (notification: any) => {
    const actionUrl = notification.action_url;
    const followerIdMatch = actionUrl?.match(/follower_id=([^&]+)/);
    const followerId = followerIdMatch?.[1];
    
    if (followerId) {
      onNavigate("profile", { userId: followerId });
    }
  };

  const handleChatClick = (chat: Chat) => {
    if (swipedChatId === chat.id) {
      setSwipedChatId(null);
      return;
    }
    
    onNavigate("chat-screen", { 
      chatId: chat.id, 
      partnerName: chat.other_user?.full_name || t('common.user'),
      partnerAvatar: chat.other_user?.avatar_url,
      partnerId: chat.other_user?.id
    });
  };

  const handleStartConversation = async (contact: ChatContact) => {
    try {
      const chatId = await createOrGetChat(contact.id);
      setNewConversationOpen(false);
      onNavigate("chat-screen", {
        chatId,
        partnerName: contact.name,
        partnerAvatar: contact.avatar_url,
        partnerId: contact.id
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error(t('notifications.chatStartError'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onNavigate("main")}
          className="p-2"
        >
          <ArrowLeft className="h-5 w-5" style={{ color: '#3D6B4F' }} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-primary">
            {mode === 'notifications' ? t('notifications.title') : t('notifications.chats')}
          </h1>
          <p className="mt-1" style={{ color: '#3D6B4F', opacity: 0.7 }}>
            {mode === 'notifications' 
              ? t('notifications.subtitle') 
              : t('notifications.chatsSubtitle')
            }
          </p>
        </div>
      </div>

      {mode === 'notifications' && (
        <div className="space-y-3">
          {notifications.length > 0 && (
            <></>
          )}
          
          {notifications.map((notification) => {
            const IconComponent = getNotificationIcon(notification.type);
            const colors = getNotificationColors(notification.type);
            const timeAgo = new Date(notification.created_at).toLocaleString();
            
            return (
              <div key={notification.id} className="relative overflow-hidden rounded-lg">
                {/* Delete button background */}
                <div 
                  className={`absolute inset-y-0 right-0 flex items-center justify-center 
                              bg-destructive transition-all duration-200
                              ${swipedId === notification.id ? 'w-20' : 'w-0'}`}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(notification.id)}
                    className="w-full h-full text-destructive-foreground hover:bg-destructive/90 rounded-none"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
                
                {/* Notification card that slides */}
                <Card 
                  className={`bg-card/80 backdrop-blur-sm hover:shadow-md transition-all duration-200 ${
                    !notification.read ? 'border-primary/30' : ''
                  } ${swipedId === notification.id ? '-translate-x-20' : 'translate-x-0'}`}
                  onTouchStart={(e) => onTouchStart(e, notification.id)}
                  onTouchMove={onTouchMove}
                  onTouchEnd={() => onTouchEnd(notification.id)}
                >
                  <CardContent className="p-2.5">
                    <div className="flex items-start space-x-2.5">
                      <div className={`p-1.5 rounded-full ${colors.bgColor}`}>
                        <IconComponent className={`h-4 w-4 ${colors.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="font-medium text-sm text-foreground">{notification.title}</p>
                          <div className="flex items-center space-x-1.5">
                            <span className="text-xs text-muted-foreground">{timeAgo}</span>
                            {!notification.read && (
                              <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1.5">{notification.message}</p>
                        
                        {notification.type === 'partner_invitation' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className="flex items-center gap-1 text-destructive hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                              {t('notifications.reject')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className="flex items-center gap-1 bg-primary hover:bg-primary/90"
                            >
                              <Check className="h-3 w-3" />
                              {t('notifications.register')}
                            </Button>
                          </div>
                        )}

                        {notification.type === 'split_payment' && (
                          <div className="flex gap-2 mt-1">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className="flex items-center gap-1 bg-primary hover:bg-primary/90"
                            >
                              <Trophy className="h-3 w-3" />
                              Ir ahora
                            </Button>
                          </div>
                        )}

                        {notification.type === 'follow_notification' && (() => {
                          const actionUrl = notification.action_url;
                          const followerIdMatch = actionUrl?.match(/follower_id=([^&]+)/);
                          const followerId = followerIdMatch?.[1];
                          const relationshipStatus = followerId ? getRelationshipStatus(followerId) : 'none';
                          const shouldShowFollowBack = relationshipStatus === 'pending_from_them' || relationshipStatus === 'they_follow_me';
                          
                          return (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewProfile(notification);
                                }}
                                className="flex items-center gap-1"
                              >
                                <User className="h-3 w-3" />
                                {t('notifications.viewProfile')}
                              </Button>
                              
                              {shouldShowFollowBack ? (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFollowBack(notification);
                                  }}
                                  className="flex items-center gap-1 bg-primary hover:bg-primary/90"
                                >
                                  <UserPlus className="h-3 w-3" />
                                  {t('notifications.followBack')}
                                </Button>
                              ) : relationshipStatus === 'mutual_following' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled
                                  className="flex items-center gap-1"
                                >
                                  <Check className="h-3 w-3" />
                                  {t('profile.friends')}
                                </Button>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
          
          {notifications.length === 0 && (
            <Card className="bg-card/80 backdrop-blur-sm">
              <CardContent className="p-12 text-center">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">{t('notifications.noNotificationsTitle')}</h3>
                <p className="text-muted-foreground">{t('notifications.noNotificationsDesc')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {mode === 'chats' && (
        <div className="space-y-4">
          {/* New conversation button */}
          <Button
            onClick={() => setNewConversationOpen(true)}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('notifications.newConversation')}
          </Button>

          {/* Filter tabs */}
          <Tabs value={chatFilter} onValueChange={(v) => setChatFilter(v as typeof chatFilter)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all" className="flex items-center gap-1 text-xs sm:text-sm">
                <MessageCircle className="h-3.5 w-3.5" />
                <span>{t('notifications.tabAll')}</span>
              </TabsTrigger>
              <TabsTrigger value="friends" className="flex items-center gap-1 text-xs sm:text-sm">
                <Users className="h-3.5 w-3.5" />
                <span>{t('notifications.tabFriends')}</span>
              </TabsTrigger>
              <TabsTrigger value="organizers" className="flex items-center gap-1 text-xs sm:text-sm">
                <Building2 className="h-3.5 w-3.5" />
                <span>{t('notifications.tabOrganizers')}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* New conversation drawer */}
          <NewConversationDrawer
            open={newConversationOpen}
            onOpenChange={setNewConversationOpen}
            onSelectContact={handleStartConversation}
          />

          {chatsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filteredChats.length > 0 ? (
            filteredChats.map((chat) => (
              <div key={chat.id} className="relative overflow-hidden rounded-lg">
                {/* Delete button background */}
                <div 
                  className={`absolute inset-y-0 right-0 flex items-center justify-center 
                              bg-destructive transition-all duration-200
                              ${swipedChatId === chat.id ? 'w-20' : 'w-0'}`}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteChat(chat.id)}
                    className="w-full h-full text-destructive-foreground hover:bg-destructive/90 rounded-none"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
                
                {/* Chat card that slides */}
                <Card 
                  className={`bg-card/80 backdrop-blur-sm cursor-pointer hover:shadow-md transition-all duration-200
                              ${swipedChatId === chat.id ? '-translate-x-20' : 'translate-x-0'}`}
                  onTouchStart={(e) => onChatTouchStart(e, chat.id)}
                  onTouchMove={onTouchMove}
                  onTouchEnd={() => onChatTouchEnd(chat.id)}
                  onClick={() => handleChatClick(chat)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-12 w-12">
                        {chat.other_user?.avatar_url && (
                          <AvatarImage src={chat.other_user.avatar_url} alt={chat.other_user?.full_name || t('common.user')} />
                        )}
                        <AvatarFallback className="bg-primary/10 text-primary font-medium">
                          {chat.other_user?.full_name?.split(' ').map(n => n[0]).join('') || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium text-foreground ${(chat.unread_count || 0) > 0 ? 'font-bold' : ''}`}>{chat.other_user?.full_name || t('common.user')}</p>
                            {chat.contact_type === 'organizer' && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                <Building2 className="h-3 w-3 mr-0.5" />
                                Org
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {chat.last_message_at ? formatTimeAgo(chat.last_message_at) : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className={`text-sm mt-0 truncate flex-1 ${(chat.unread_count || 0) > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {chat.last_message || t('notifications.noMessages')}
                          </p>
                          {(chat.unread_count || 0) > 0 && (
                            <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))
          ) : (
            <Card className="bg-card/80 backdrop-blur-sm">
              <CardContent className="p-12 text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">{getEmptyStateMessage().title}</h3>
                <p className="text-muted-foreground">{getEmptyStateMessage().description}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsAndChats;
